import type { MainSizesType } from '~/types/components'
import type { ControlSizeStyleType } from '~/types/controlSize'

/**
 * Escala única de los controles de formulario (campos y botones).
 *
 * Antes cada componente traía su propia mezcla de `min-h` + `py-*`, y la altura
 * real no la fijaba el `min-h` sino el padding más el `line-height`: el mismo
 * `size` daba 50 px en un input, 42 en un botón con texto, 44 en uno con icono y
 * 48 en la variante `text` (que no tiene borde). De ahí que un botón acabara
 * siendo más bajo que el campo que tenía al lado.
 *
 * Por eso la altura es **fija** (`h-*`, no `min-h-*`) y el contenido se centra
 * con flex: así la talla manda por encima de la variante, del icono y del borde.
 * Sólo los controles que crecen con su contenido (Textarea, TagInput) usan
 * `minHeight`, y ahí la escala marca el arranque.
 *
 * Las clases se escriben **literales y completas**: Tailwind escanea el fuente
 * como texto, así que una clase compuesta por interpolación no llega al CSS.
 */
export const controlSizes: Record<MainSizesType, ControlSizeStyleType> = {
  xs: {
    height: 'h-8',
    adornmentChild: '[&_button]:h-6',
    minHeight: 'min-h-8',
    text: 'text-xs',
    padX: 'px-2',
    padStart: 'start-2',
    padY: 'py-1.5',
    labelPad: 'pt-3.5 pb-0',
    labelRestingTop: 'peer-placeholder-shown:top-3.5',
    labelFloating: 'text-[10px]',
    labelPeer: 'peer-placeholder-shown:text-xs peer-focus:text-[10px]',
    icon: 20,
    messageText: 'text-xs',
    messageMinH: 'min-h-4'
  },
  sm: {
    height: 'h-9',
    adornmentChild: '[&_button]:h-7',
    minHeight: 'min-h-9',
    text: 'text-sm',
    padX: 'px-2.5',
    padStart: 'start-2.5',
    padY: 'py-2',
    labelPad: 'pt-3.5 pb-0',
    labelRestingTop: 'peer-placeholder-shown:top-3.5',
    labelFloating: 'text-[10px]',
    labelPeer: 'peer-placeholder-shown:text-sm peer-focus:text-[10px]',
    icon: 24,
    messageText: 'text-xs',
    messageMinH: 'min-h-4'
  },
  md: {
    height: 'h-10',
    adornmentChild: '[&_button]:h-8',
    minHeight: 'min-h-10',
    text: 'text-sm',
    padX: 'px-3',
    padStart: 'start-3',
    padY: 'py-2.5',
    labelPad: 'pt-4 pb-0.5',
    labelRestingTop: 'peer-placeholder-shown:top-4',
    labelFloating: 'text-[11px]',
    labelPeer: 'peer-placeholder-shown:text-sm peer-focus:text-[11px]',
    icon: 28,
    messageText: 'text-sm',
    messageMinH: 'min-h-5'
  },
  lg: {
    height: 'h-12',
    adornmentChild: '[&_button]:h-10',
    minHeight: 'min-h-12',
    text: 'text-base',
    padX: 'px-4',
    padStart: 'start-4',
    padY: 'py-3',
    labelPad: 'pt-5 pb-0.5',
    labelRestingTop: 'peer-placeholder-shown:top-5',
    labelFloating: 'text-xs',
    labelPeer: 'peer-placeholder-shown:text-base peer-focus:text-xs',
    icon: 30,
    messageText: 'text-sm',
    messageMinH: 'min-h-5'
  },
  xl: {
    height: 'h-14',
    adornmentChild: '[&_button]:h-12',
    minHeight: 'min-h-14',
    text: 'text-lg',
    padX: 'px-5',
    padStart: 'start-5',
    padY: 'py-4',
    labelPad: 'pt-6 pb-0.5',
    labelRestingTop: 'peer-placeholder-shown:top-6',
    labelFloating: 'text-sm',
    labelPeer: 'peer-placeholder-shown:text-lg peer-focus:text-sm',
    icon: 34,
    messageText: 'text-sm',
    messageMinH: 'min-h-5'
  }
}

/** Estilos de una talla, cayendo a `md` cuando llega algo que no está en la escala. */
export const controlSize = (size?: MainSizesType): ControlSizeStyleType => controlSizes[size ?? 'md'] ?? controlSizes.md

/**
 * Clases de la etiqueta flotante. En reposo va centrada sobre el texto del campo
 * (`top-1/2`) y al escribir o enfocar sube al hueco de arriba. Se escribe al
 * revés —el estado base es el flotante— porque el selector disponible es
 * `peer-placeholder-shown`, que sólo marca el campo vacío.
 */
export const floatingLabelClasses = (size?: MainSizesType): string => {
  const s = controlSize(size)
  return [
    'absolute z-10 origin-[0] block select-none whitespace-nowrap overflow-hidden text-ellipsis pointer-events-none',
    'top-1 translate-y-0 duration-100 transition-all',
    s.labelFloating,
    'peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2',
    s.labelPeer,
    'peer-focus:top-1 peer-focus:translate-y-0'
  ].join(' ')
}
